import {
  BootstrapSizes,
  Box,
  Button,
  ButtonColors,
  Grid,
  Modal,
  Row,
  Typography,
} from "@talentech/components";
import { useEffect, useState } from "react";
import { useEditor } from "@grapesjs/react";
import { Editor } from "grapesjs";
import { getFaviconValues } from "@/shared/openGraph";
import { CustomCommands } from "@/shared/utils";
import clsx from "clsx";
import { useGetFaviconsQuery } from "@/redux/reducer/favicons.api";
import { IMediaFile } from "@/service/service";

const customDataKey = "favicon";

const FavIconModal: React.FC = () => {
  const editor = useEditor();

  const [selectedFavicon, setFavicon] = useState<IMediaFile | null>(null);

  const initialValues = getFaviconValues(editor);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    !showModal && setFavicon(null);
    showModal && initialValues && setFavicon(initialValues);
  }, [showModal, initialValues]);

  useEffect(() => {
    editor.Commands.add(CustomCommands.MODAL_FAVICON_OPEN, () =>
      setShowModal(true)
    );
    editor.Commands.add(CustomCommands.MODAL_FAVICON_CLOSE, () =>
      setShowModal(false)
    );
    editor.Commands.add(
      CustomCommands.MODAL_FAVICON_SAVE,
      async (editor: Editor, _ed: any, { form }: { form: IMediaFile }) => {
        await editor.DataSources.remove(customDataKey);
        await editor.DataSources.add({
          id: customDataKey,
          records: [
            {
              id: "meta",
              ...form,
            },
          ],
        });
        setShowModal(false);
      }
    );
  }, []);

  const { favicons } = useGetFaviconsQuery(
    {},
    {
      selectFromResult: ({ data, ...rest }) => {
        return {
          favicons: data?.files || [],
          ...rest,
        };
      },
    }
  );

  const modalProps = {
    title: "Favicon",
    showModal,
    size: BootstrapSizes.XLARGE,
    toggleModal: () => editor.runCommand(CustomCommands.MODAL_FAVICON_CLOSE),
    buttons: [
      <Button
        key="cancel"
        onClick={() => editor.runCommand(CustomCommands.MODAL_FAVICON_CLOSE)}
        label="Cancel"
      />,
      <Button
        key="save"
        type="submit"
        onClick={() =>
          editor.runCommand(CustomCommands.MODAL_FAVICON_SAVE, {
            form: selectedFavicon,
          })
        }
        label="Save"
        color={ButtonColors.PRIMARY}
      />,
    ],
  };

  return (
    <Modal {...modalProps}>
      {showModal && (
        <Box className="container">
          <Box className="row">
            <Box className="col-12">
              <div className="browser-mockup">
                <div className="browser-header">
                  <div className="tab inactive">
                    <i className="fa-sharp fa-light fa-globe me-2" />
                    Other Page
                  </div>
                  <div className="tab inactive">
                    <i className="fa-sharp fa-light fa-globe me-2" />
                    Another Site
                  </div>
                  <div className="tab active pe-3">
                    {selectedFavicon ? (
                      <img src={selectedFavicon?.link} alt="Favicon" />
                    ) : (
                      <i className="fa-sharp fa-light fa-globe me-2" />
                    )}
                    My Career Site
                  </div>
                  <div className="tab inactive">
                    <i className="fa-sharp fa-light fa-globe me-2" />
                    Docs
                  </div>
                </div>

                <div className="px-3">
                  <div className="address-bar">
                    <i className="fa-sharp fa-light fa-lock me-2" />
                    https://www.example.com
                  </div>
                </div>

                <div className="browser-content">
                  <p className="lead">
                    This is a preview of your favicon in a browser tab.
                  </p>
                </div>
              </div>
            </Box>
          </Box>

          <Box className="row mt-4">
            <Box className="col-12">
              <Row>
                <Typography variant="h6" className="mb-0">
                  Select favicon:
                </Typography>
                {favicons.map((favicon) => (
                  <Grid xs={4} sm={1} key={favicon.name}>
                    <Box className="position-relative">
                      <img
                        src={favicon.link}
                        alt={favicon.name}
                        className={clsx("img-fluid cursor-pointer", {
                          "border border-success-400 p-1 rounded bg-success":
                            favicon.link === selectedFavicon?.link,
                        })}
                        onClick={() => {
                          setFavicon(favicon);
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Row>
            </Box>
          </Box>
        </Box>
      )}
    </Modal>
  );
};

export default FavIconModal;
